import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import UserInput from '@/components/_custom/UserInput/userInput.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { SET_NOTIFICATION_SUGGEST_SETTINGS } from '@/modules/API/daycare/daycareSuggestHandler.ts'
import styles from './notificationSuggest.module.scss'
import validator from 'validator'

interface AddContactEmailDialogProps {
    open: boolean
    handleClose: () => void
    daycareId: number
    currentEmails: string[]
    notification: boolean
    onSuccess: (newEmails: string[]) => void
}

const AddContactEmailDialog: FunctionComponent<AddContactEmailDialogProps> = ({
    open,
    handleClose,
    daycareId,
    currentEmails,
    notification,
    onSuccess
}: AddContactEmailDialogProps) => {
    const { t } = useTranslation()
    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [email, setEmail] = useState<string>('')

    const newEmails: string[] = [...currentEmails, email]

    const [addContactEmail] = useMutation(SET_NOTIFICATION_SUGGEST_SETTINGS, {
        variables: { daycareId, contactEmails: newEmails, notification },
        onCompleted: () => {
            onSuccess(newEmails)
            handleClose()
            setEmail('')
            showNotificationSuccess(t('contactEmailAdded'))
        },
        onError: () => {
            showNotificationError(t('contactEmailAddedError'))
        }
    })

    const isValid: boolean = validator.isEmail(email) && !currentEmails.includes(email)

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('addContactEmail')} />
            <DialogBody className={styles.DialogBody}>
                <UserInput
                    label={t('email')}
                    value={email}
                    setValue={setEmail}
                    type={'text'}
                    unallowedCharacters={'email'}
                    className={styles.UserInput}
                    fullWidth
                />
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'add'}
                        disabled={!isValid}
                        onClick={() => addContactEmail()}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default AddContactEmailDialog
